import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { CaseService } from 'src/crs/case/service/CaseService';
import { caseDtoToCaseManager } from 'src/careflow/common/common-utils';
import { TABLE_HEAD_DISPOSITION_REFERRAL_CASE } from 'src/careflow/common/table-head';
import { TableHeadCustom } from 'src/components/table';
import { CaseForm } from './CaseForm';
import { format } from 'date-fns';

export const CaseSelectionDialog = ({
  open,
  onClose,
  patient,
  onCaseSelected,
  configurations,
}: any) => {
  const [cases, setCases] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCaseForm, setShowCaseForm] = useState(false);

  const fetchCases = async () => {
    if (!patient?.id) return;
    setIsLoading(true);
    try {
      const result = await CaseService.getAll({
        limit: 50,
        filter: {
          indexedSubjectFhirRefUri: `${configurations?.fhir?.baseUrl}/Patient/${patient.id}`,
          indexedStatus: 'active',
        },
      });
      if (result?.data) {
        setCases(result.data.map(caseDtoToCaseManager));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchCases();
  }, [open, patient?.id]);

  const handleSelect = (row: any) => {
    onCaseSelected(row);
    onClose();
  };

  const handleNewCase = async (caseData: any) => {
    const result = await CaseService.create(caseData);
    if (result) {
      // result is usually the new resource
      onCaseSelected(result);
      onClose();
    }
    return result;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Select an Open Case or Create New
        <Button variant="contained" size="small" onClick={() => setShowCaseForm(true)}>
          New Case
        </Button>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 'none' }}>
            <Table size="small">
              <TableHeadCustom headLabel={TABLE_HEAD_DISPOSITION_REFERRAL_CASE} />
              <TableBody>
                {cases.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.internalNumber || row.id}</TableCell>
                    <TableCell>
                      {row.date ? format(new Date(row.date), 'MMM dd, yyyy') : 'N/A'}
                    </TableCell>
                    <TableCell>{row.owner || 'Unassigned'}</TableCell>
                    <TableCell>{row.workflowScope}</TableCell>
                    <TableCell>{row.workflowStageName}</TableCell>
                    <TableCell align="center">
                      <Button variant="outlined" size="small" onClick={() => handleSelect(row)}>
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {cases.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No open cases found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <CaseForm
        open={showCaseForm}
        onClose={() => setShowCaseForm(false)}
        patient={patient}
        carePlan={null}
        handleCase={handleNewCase}
        existData={cases?.length > 0 ? true : false}
      />
    </Dialog>
  );
};
